/**
 * Get initials from a name string
 * @param name - The name to extract initials from
 * @param maxLength - Maximum number of initials to return (default: 2)
 * @returns The initials (e.g., "John Doe" -> "JD")
 */
export const getInitials = (name: string | null | undefined, maxLength: number = 2): string => {
  if (!name || typeof name !== 'string') {
    return '?';
  }

  const words = name.trim().split(/\s+/).filter(word => word.length > 0);
  
  if (words.length === 0) {
    return '?';
  }

  if (words.length === 1) {
    // Single word: take first maxLength characters
    return words[0].substring(0, maxLength).toUpperCase();
  }

  // Multiple words: take first letter of first maxLength words
  return words
    .slice(0, maxLength)
    .map(word => word[0])
    .join('')
    .toUpperCase();
};

// Color palette for avatar backgrounds (alphabetically consistent)
const AVATAR_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#EAB308', // Yellow
  '#84CC16', // Lime
  '#22C55E', // Green
  '#10B981', // Emerald
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink
  '#F43F5E', // Rose
  '#64748B', // Slate
];

/**
 * Deterministically pick an avatar background color by name.
 * Keeps the same behavior across the app (e.g. participants + comments).
 */
export const getAvatarColorForName = (name: string | null | undefined): string => {
  const safeName = (name || '').trim();
  if (!safeName) {
    return AVATAR_COLORS[0];
  }

  const firstLetter = safeName.charAt(0).toUpperCase();
  const charCode = firstLetter.charCodeAt(0);
  const index = (charCode - 65) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index >= 0 ? index : 0];
};

